import type {
  BookingCancellationQuoteResponse,
  BookingCancellationResponse,
  BookingResponse,
} from "../types/generated";
import { moneyToCents } from "../types/money-brand";
import { parseDecimal } from "../utils/money";
import { isApiError } from "./errors";

const EXACT_RETRY_CODES = new Set([
  "PAYMENT_STATE_UNCERTAIN",
  "CANCELLATION_CALENDAR_CLEANUP_UNCERTAIN",
  "CANCELLATION_STATE_UNCERTAIN",
]);

function ratePercentToBasisPoints(value: number | string): number | null {
  const percentage = parseDecimal(value);
  if (percentage === null || percentage < 0 || percentage > 100) return null;
  const scaled = percentage * 100;
  const basisPoints = Math.round(scaled);
  if (
    !Number.isSafeInteger(basisPoints) ||
    Math.abs(scaled - basisPoints) > 1e-8
  ) {
    return null;
  }
  return basisPoints;
}

/** Runtime validation for a server-issued booking cancellation decision. */
export function isValidBookingCancellationQuote(
  quote: BookingCancellationQuoteResponse | undefined,
  bookingId: string,
): quote is BookingCancellationQuoteResponse {
  if (
    !quote ||
    quote.booking_id !== bookingId ||
    !quote.quote_id ||
    !quote.policy_id ||
    !Number.isInteger(quote.policy_version) ||
    quote.policy_version < 1 ||
    !/^[A-Z]{3}$/i.test(quote.currency) ||
    !Number.isFinite(Date.parse(quote.calculated_at)) ||
    !Number.isFinite(Date.parse(quote.expires_at)) ||
    Date.parse(quote.expires_at) <= Date.parse(quote.calculated_at) ||
    quote.applied_window === undefined
  ) {
    return false;
  }

  const amount = moneyToCents(quote.amount_charged);
  const fee = moneyToCents(quote.fee_amount);
  const refund = moneyToCents(quote.refund_amount);
  const percentageBasisPoints = ratePercentToBasisPoints(quote.fee_percentage);
  const appliedWindow = quote.applied_window;
  const appliedPercentageBasisPoints = appliedWindow
    ? ratePercentToBasisPoints(appliedWindow.fee_percentage)
    : null;
  const appliedWindowIsValid = appliedWindow
    ? Number.isInteger(appliedWindow.hours_before) &&
      appliedWindow.hours_before >= 0 &&
      appliedPercentageBasisPoints === percentageBasisPoints
    : percentageBasisPoints === 0;
  return (
    amount !== null &&
    amount >= 0 &&
    fee !== null &&
    fee >= 0 &&
    fee <= amount &&
    refund !== null &&
    refund >= 0 &&
    refund <= amount &&
    fee + refund === amount &&
    percentageBasisPoints !== null &&
    appliedWindowIsValid
  );
}

export function isBookingCancellationQuoteExpired(
  quote: BookingCancellationQuoteResponse,
  now = Date.now(),
): boolean {
  const expiresAt = Date.parse(quote.expires_at);
  return !Number.isFinite(expiresAt) || expiresAt <= now;
}

/** True when changing any part of a retry could duplicate or misstate money. */
export function requiresExactCancellationRetry(error: unknown): boolean {
  if (error === null || error === undefined) return false;
  // A transport rejection has no response proving whether the server committed.
  if (!isApiError(error)) return true;
  return (
    error.status >= 500 ||
    error.details?.retry_same_request === true ||
    error.details?.retryable === true ||
    EXACT_RETRY_CODES.has(error.code)
  );
}

/** Apply committed fields without discarding display data from an enriched read. */
export function mergeBookingCancellationResponse(
  existing: BookingResponse,
  committed: BookingCancellationResponse,
): BookingResponse {
  return {
    ...existing,
    ...committed,
    display_name: committed.display_name ?? existing.display_name,
  };
}
